import classNames from 'classnames'
import React, { ChangeEvent, InputHTMLAttributes, useRef } from 'react'

import { Button } from '../../button/button.js'
import { CheckCircleLargeIcon, CloseIcon } from '../../icon/icon.js'

export interface FileUploadProps {
    /**
     * File add button text.
     */
    addText: string
    /**
     * ID attribute.
     */
    id: string
    /**
     * The label value.
     */
    label: string
    /**
     * File remove button text.
     */
    removeText: string
    /**
     * Additional class.
     */
    className?: string
    /**
     * The files of input. Should use with onChange prop.
     * When FileUpload is disabled, onChange is not required.
     */
    files?: FileUploadFiles
    /**
     * Additional input attributes.
     */
    input?: InputHTMLAttributes<HTMLInputElement>
    /**
     * Allow mutliple files.
     */
    multiple?: boolean
    /**
     * onChange callback handler containing event and filelist object.
     */
    onChange?: (event: ChangeEvent<HTMLInputElement> | null, files: FileUploadFiles) => void
    /**
     * onRemove callback handler.
     */
    onRemove?: (value: File) => void
}

export type FileUploadFiles = File[]

export const FileUpload = (props: FileUploadProps): JSX.Element => {
    const element = useRef<HTMLInputElement | null>(null)
    const { addText, files, input, id, multiple, removeText } = props

    const className: string = classNames('file-upload', props.className)

    const getFileList = (
        event: React.ChangeEvent<HTMLInputElement> & {
            dataTransfer?: DataTransfer
        },
    ): FileUploadFiles => {
        let fileList: FileList | DataTransferItemList | null = null
        const fileEntities: FileUploadFiles = []

        if (event.dataTransfer) {
            const dt = event.dataTransfer

            if (dt.files?.length) {
                fileList = dt.files
            } else if (dt.items?.length) {
                fileList = dt.items
            }
        } else if (event.target?.files) {
            fileList = event.target.files
        }

        Array.prototype.slice.call(fileList).map((file: File) => {
            fileEntities.push(file)
        })

        return fileEntities
    }

    const onChange = (event: ChangeEvent<HTMLInputElement>) => {
        const newFiles: FileUploadFiles = getFileList(event)

        if (props.input?.max) {
            const max = parseInt(props.input.max + '', 10)
            if (newFiles.length > max) {
                newFiles.splice(max, newFiles.length - max)
            }
        }

        if (props.onChange) {
            props.onChange(event, newFiles)
        }
    }

    const onRemove = (file: File) => {
        if (props.onRemove) {
            props.onRemove(file)
        }
    }

    const renderFiles = (items: FileUploadFiles) => (
        <ul className="file-upload__list">
            {items.map((file, i) => {
                return renderFile(file, i)
            })}
        </ul>
    )

    const renderFile = (file: File, index: number) => {
        return (
            <li className="file-upload__list-item" key={index}>
                <CheckCircleLargeIcon className="file-upload__icon" />
                <span className="file-upload__item-name text-small">{file.name}</span>
                <Button
                    className="file-upload__item-remove"
                    icon={CloseIcon}
                    iconClassName="button__icon--large"
                    onClick={() => onRemove(file)}
                    text={removeText}
                />
            </li>
        )
    }

    const renderUploadButton = () => (
        <Button className="file-upload__add-button" isBlock={true} onClick={onAdd} text={addText} variant="secondary" />
    )

    const onAdd = () => {
        if (element.current) {
            element.current.click()
        }
    }

    return (
        <div className={className}>
            <label htmlFor={id} className="file-upload__label">
                {props.label}
            </label>
            <div className="file-upload__inner">
                <input
                    {...input}
                    className="file-upload__input"
                    id={id}
                    onChange={onChange}
                    ref={(el) => {
                        element.current = el
                    }}
                    type="file"
                    multiple={multiple}
                />
                {files && files.length > 0 && renderFiles(files)}
                <div className="file-upload__add">{renderUploadButton()}</div>
            </div>
        </div>
    )
}
